import { Loader2, RotateCcw, Search } from 'lucide-react';
import { useEffect, type FormEvent } from 'react';
import { cn } from '@/lib/cn';
import type { TimeZoneMode } from '@/lib/format';
import type { BusinessClientSummary } from '@/types';
import { ClientSelect } from '@/features/bet-inspection/components/ClientSelect';
import { toHourInput, fromHourInput } from '../lib/time';

/** `currency: ''` means ALL currencies, converted to the global base. */
export interface ReportFilters {
  psid: string;
  currency: string;
  /** UTC instants — see lib/time.ts for why these are not display strings. */
  from: number;
  to: number;
  playerId: string;
  live: boolean;
}

/**
 * Same compact command-bar language as the console's other lookup bars.
 *
 * Dates are hour-granular (the rollup is hourly, so minutes would imply
 * precision the data lacks) and are rendered in whichever zone the navbar is
 * set to, while the underlying instant never moves.
 */
export function ReportFilterBar({
  value,
  onChange,
  onSearch,
  onReset,
  loading,
  clients,
  clientsLoading,
  clientsError,
  currencies,
  tz,
  base,
  showPlayer = true,
}: {
  value: ReportFilters;
  onChange: (patch: Partial<ReportFilters>) => void;
  onSearch: () => void;
  onReset: () => void;
  loading: boolean;
  clients: BusinessClientSummary[];
  clientsLoading: boolean;
  clientsError: boolean;
  currencies: string[];
  tz: TimeZoneMode;
  base: string;
  /** The daily finance report has no per-player dimension, so it hides this. */
  showPlayer?: boolean;
}) {
  const canSearch = Boolean(value.psid && value.from && value.to && value.to > value.from);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (canSearch) onSearch();
  };

  // A currency that vanished (project changed) must not silently keep filtering.
  useEffect(() => {
    if (value.currency && currencies.length && !currencies.includes(value.currency)) {
      onChange({ currency: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencies.join('|')]);

  return (
    <form onSubmit={submit} className="flex flex-wrap items-center gap-1.5 border-b border-hairline px-4 py-2.5">
      <ClientSelect
        value={value.psid}
        onChange={(psid) => onChange({ psid })}
        clients={clients}
        loading={clientsLoading}
        error={clientsError}
      />

      <label className="relative block w-[168px]">
        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 font-condensed text-[9px] uppercase tracking-wider text-text-faint">
          currency
        </span>
        <select
          value={value.currency}
          onChange={(e) => onChange({ currency: e.target.value })}
          title={
            value.currency
              ? `Exact ${value.currency} figures — no conversion, base currency ignored`
              : `All currencies converted to ${base}`
          }
          className={cn(
            'w-full appearance-none border border-hairline bg-surface-sunken py-1.5 pl-[64px] pr-2',
            'font-mono text-[12px] text-text hover:border-hairline-strong focus:border-accent focus:outline-none',
          )}
        >
          <option value="">All → {base}</option>
          {currencies.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>

      <HourField
        label="from"
        ms={value.from}
        tz={tz}
        onChange={(from) => onChange({ from })}
        width="w-[195px]"
      />
      <HourField
        label="to"
        ms={value.to}
        tz={tz}
        onChange={(to) => onChange({ to })}
        width="w-[185px]"
      />

      {showPlayer && (
        <TextField label="player" value={value.playerId} onChange={(playerId) => onChange({ playerId })} width="w-[140px]" />
      )}

      <button
        type="button"
        onClick={() => onChange({ live: !value.live })}
        title={
          value.live
            ? 'Realtime mirror — current, but only successfully-processed transactions'
            : 'Settled rollup — authoritative, about an hour behind'
        }
        className={cn(
          'inline-flex items-center gap-1.5 border px-2.5 py-1.5 font-condensed text-[11px] font-semibold uppercase tracking-wider',
          value.live
            ? 'border-warn/40 bg-warn-bg text-warn'
            : 'border-hairline text-text-dim hover:border-hairline-strong',
        )}
      >
        <span className={cn('h-2 w-2 rounded-full', value.live ? 'bg-warn' : 'bg-transparent ring-1 ring-hairline-strong')} />
        {value.live ? 'live' : 'settled'}
      </button>

      <button
        type="button"
        onClick={onReset}
        className="inline-flex items-center gap-1.5 border border-hairline px-2.5 py-1.5 font-condensed text-[11px] font-semibold uppercase tracking-wider text-text-dim hover:border-hairline-strong hover:text-text"
      >
        <RotateCcw size={12} /> Reset
      </button>
      <button
        type="submit"
        disabled={!canSearch || loading}
        className={cn(
          'inline-flex items-center gap-1.5 border border-accent/40 bg-accent/10 px-3 py-1.5',
          'font-condensed text-[11px] font-semibold uppercase tracking-wider text-accent',
          'hover:bg-accent/20 disabled:cursor-not-allowed disabled:opacity-40',
        )}
      >
        {loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
        Search
      </button>

      <span className="ml-auto hidden font-condensed text-[10px] uppercase tracking-wider text-text-faint xl:block">
        hourly · {tz}
      </span>
    </form>
  );
}

/**
 * Hour-granular datetime field. `step=3600` makes browsers hide/disable the
 * minute spinner, and anything typed is floored to the hour on the way in.
 */
function HourField({
  label,
  ms,
  tz,
  onChange,
  width,
}: {
  label: string;
  ms: number;
  tz: TimeZoneMode;
  onChange: (ms: number) => void;
  width: string;
}) {
  return (
    <label className={cn('relative block', width)}>
      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 font-condensed text-[9px] uppercase tracking-wider text-text-faint">
        {label}
      </span>
      <input
        type="datetime-local"
        step={3600}
        value={toHourInput(ms, tz)}
        onChange={(e) => {
          const next = fromHourInput(e.target.value, tz);
          if (next !== null) onChange(next);
        }}
        className={cn(
          'w-full border border-hairline bg-surface-sunken py-1.5 pr-2',
          'font-mono text-[12px] text-text',
          'hover:border-hairline-strong focus:border-accent focus:outline-none',
          '[color-scheme:dark]',
        )}
        style={{ paddingLeft: `${label.length * 5 + 16}px` }}
      />
    </label>
  );
}

function TextField({
  label,
  value,
  onChange,
  width,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  width: string;
}) {
  return (
    <label className={cn('relative block', width)}>
      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 font-condensed text-[9px] uppercase tracking-wider text-text-faint">
        {label}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        autoComplete="off"
        className={cn(
          'w-full border border-hairline bg-surface-sunken py-1.5 pr-2',
          'font-mono text-[12px] text-text placeholder:text-text-faint',
          'hover:border-hairline-strong focus:border-accent focus:outline-none',
        )}
        style={{ paddingLeft: `${label.length * 5 + 16}px` }}
      />
    </label>
  );
}
